import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase/client';

interface CoverageMetrics {
  average_coverage_days: number;
  critical_items_count: number;
  last_mrp_run_date?: string;
  last_mrp_run_id?: string;
  warehouse_id?: string;
  total_items_analyzed: number;
}

export const useMRPCoverage = () => {
  const [coverageMetrics, setCoverageMetrics] = useState<CoverageMetrics>({
    average_coverage_days: 0,
    critical_items_count: 0,
    total_items_analyzed: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCoverageMetrics = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Get the most recent completed MRP run
      const { data: latestRun, error: runError } = await supabase
        .from('mrp_runs')
        .select('id, created_at, warehouse_id')
        .eq('status', 'COMPLETED')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (runError || !latestRun) {
        setCoverageMetrics({
          average_coverage_days: 0,
          critical_items_count: 0,
          total_items_analyzed: 0
        });
        return;
      }

      // Get shortage data from the latest MRP run to calculate coverage
      // mrp_shortages table has: item_id, net_shortage_qty, need_by_date, mrp_run_id
      const { data: shortages, error: shortageError } = await supabase
        .from('mrp_shortages')
        .select(`
          item_id,
          net_shortage_qty,
          need_by_date,
          item:item_id(
            id,
            drawing_number
          )
        `)
        .eq('mrp_run_id', latestRun.id);

      if (shortageError) {
        throw shortageError;
      }

      // Calculate coverage metrics
      let totalCoverageDays = 0;
      let criticalItemsCount = 0;
      const itemsAnalyzed = shortages?.length || 0;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      shortages?.forEach((shortage: any) => {
        const shortageQty = shortage.net_shortage_qty || 0;
        const needByDate = shortage.need_by_date ? new Date(shortage.need_by_date) : null;
        
        if (shortageQty > 0 && needByDate) {
          // Calculate days until needed
          const today = new Date();
          const daysUntilNeeded = Math.max(0, Math.ceil((needByDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)));
          totalCoverageDays += daysUntilNeeded;
          
          // Items needed within 7 days are critical
          if (daysUntilNeeded < 7) {
            criticalItemsCount++;
          }
        }
      });

      const averageCoverage = itemsAnalyzed > 0 ? totalCoverageDays / itemsAnalyzed : 0;

      setCoverageMetrics({
        average_coverage_days: Math.round(averageCoverage * 10) / 10, // Round to 1 decimal
        critical_items_count: criticalItemsCount,
        last_mrp_run_date: latestRun.created_at,
        last_mrp_run_id: latestRun.id,
        warehouse_id: latestRun.warehouse_id,
        total_items_analyzed: itemsAnalyzed
      });

    } catch (err) {
      console.error('Error fetching MRP coverage metrics:', err);
      setError('Failed to load coverage metrics');
      setCoverageMetrics({
        average_coverage_days: 0,
        critical_items_count: 0,
        total_items_analyzed: 0
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoverageMetrics();
  }, []);

  return {
    coverageMetrics,
    loading,
    error,
    refreshCoverage: fetchCoverageMetrics
  };
};
